<template>
  <input
    :id="props.id"
    :ref="inputRef"
    v-model="inputValue"
    class="tw-block tw-w-full tw-rounded tw-border tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all"
    :class="[classes]"
    :name="props.name"
    :type="props.type"
    :autofocus="props.autofocus"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :placeholder="props.placeholder"
    :data-test-id="props.testId"
    :step="props.step"
    :min="props.min"
    :max="props.max"
    :maxlength="props.maxlength"
    :autocomplete="props.autocomplete"
    :data-keeper-ignore="props.dataKeeperIgnore"
    :data-bitwarden-ignore="props.dataBitwardenIgnore"
    :data-1password-ignore="props.dataOnepasswordIgnore"
    @blur="emitBlur"
    @keyup="emitKeyup"
    @keydown="emitKeydown"
    @paste="emitPaste"
  />
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";

import type { InputType, Size } from "../../components/types";

export interface FormInputProps {
  inputRef?: string;
  id: string;
  name: string;
  value: string | number;
  size?: Size;
  type?: InputType;
  placeholder?: string;
  state?: boolean | null;
  disabled?: boolean;
  readonly?: boolean;
  autofocus?: boolean;
  testId?: string;
  step?: number;
  min?: number | string;
  max?: number | string;
  maxlength?: number;
  autocomplete?: string;
  dataKeeperIgnore?: string;
  dataBitwardenIgnore?: string;
  dataOnepasswordIgnore?: string;
}

const props = withDefaults(defineProps<FormInputProps>(), {
  size: "medium",
  type: "text",
  placeholder: "",
  state: null,
  disabled: false,
  readonly: false,
  autofocus: false,
  testId: "",
  step: undefined,
  min: undefined,
  max: undefined,
  maxlength: undefined,
  inputRef: undefined,
  autocomplete: undefined,
  dataKeeperIgnore: undefined,
  dataBitwardenIgnore: undefined,
  dataOnepasswordIgnore: undefined,
});

const emit = defineEmits<{
  (event: "update:value", value: string | number): void;
  (event: "on-blur"): void;
  (event: "on-keyup", value: KeyboardEvent): void;
  (event: "on-keydown", value: KeyboardEvent): void;
  (event: "on-paste", value: ClipboardEvent): void;
}>();

const inputValue = ref(props.value);

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-3",
  medium: "tw-text-base tw-py-2 tw-px-3",
  large: "tw-text-lg tw-py-3 tw-px-3",
};

const states = {
  none: "tw-border-dorian-gray-100/80",
  valid: "tw-border-success-500",
  error: "tw-border-danger-500",
};

const focusClasses =
  "focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10";

const disabledClasses =
  "disabled:tw-cursor-default disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none";

const readonlyClasses =
  "read-only:tw-cursor-not-allowed read-only:tw-bg-dorian-gray-50 read-only:tw-text-dorian-gray-500 read-only:tw-outline-none";

const classes = computed(() => {
  return [
    sizes[props.size],
    props.state === null
      ? states.none
      : props.state
        ? states.valid
        : states.error,
    focusClasses,
    disabledClasses,
    readonlyClasses,
  ];
});

watch(
  () => props.value,
  () => {
    inputValue.value = props.value;
  },
);

watch(inputValue, (newValue) => {
  emit("update:value", newValue);
});

const emitBlur = (): void => {
  if (typeof inputValue.value === "string") {
    inputValue.value = inputValue.value.trim();
  }
  emit("on-blur");
};

const emitKeyup = (value: KeyboardEvent): void => {
  emit("on-keyup", value);
};

const emitKeydown = (value: KeyboardEvent): void => {
  emit("on-keydown", value);
};

const emitPaste = (value: ClipboardEvent): void => {
  emit("on-paste", value);
};
</script>
